import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  Check,
  Building2,
  Package,
  Users,
  Printer,
  Sparkles,
} from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { cn } from '../../lib/cn';
import { useBrand } from '../../brand/BrandProvider';

const ITEMS = [
  {
    id: 'profile',
    label: 'Set up your business profile',
    sub: 'Logo, address, tax registration',
    icon: Building2,
  },
  {
    id: 'products',
    label: 'Add your products',
    sub: 'Import a CSV or add them one by one',
    icon: Package,
  },
  {
    id: 'team',
    label: 'Invite your cashier',
    sub: 'Send a magic link by email',
    icon: Users,
  },
  {
    id: 'printer',
    label: 'Connect a printer',
    sub: '80mm thermal works out of the box',
    icon: Printer,
  },
];

/**
 * Welcome — §5.7
 * One-time first-login screen. Shown right after provisioning completes.
 */
export function Welcome() {
  const brand = useBrand();
  const [done, setDone] = useState<Record<string, boolean>>({});

  function toggle(id: string) {
    setDone((d) => ({ ...d, [id]: !d[id] }));
  }

  const completed = Object.values(done).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark relative flex items-center justify-center px-4 py-12">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        <AuthThemeToggle />
      </div>
      <div className="w-full max-w-2xl">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 grid place-items-center rounded-xl bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 mb-4">
            <Sparkles className="w-6 h-6" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-center">
            Welcome to {brand.name}, Imran
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 text-center max-w-md">
            Your POS at{' '}
            <span className="mono font-medium text-zinc-800 dark:text-zinc-100">
              khan-bakery.itcitysoft.com
            </span>{' '}
            is ready.
          </p>
        </div>

        <Card className="p-5 sm:p-7 mb-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
              A few things you can do now
            </h2>
            <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
              {completed} / {ITEMS.length} done
            </span>
          </div>

          <ul className="space-y-2">
            {ITEMS.map((item) => {
              const Icon = item.icon;
              const isDone = !!done[item.id];
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => toggle(item.id)}
                    className={cn(
                      'w-full flex items-center gap-3 p-3 rounded-md text-left transition border',
                      isDone
                        ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900'
                        : 'bg-white border-zinc-200 hover:bg-zinc-50 dark:bg-zinc-950 dark:border-zinc-700 dark:hover:bg-zinc-900',
                    )}
                  >
                    <span
                      className={cn(
                        'w-6 h-6 grid place-items-center rounded-full border-2 transition flex-shrink-0',
                        isDone
                          ? 'bg-emerald-600 border-emerald-600 text-white'
                          : 'border-zinc-300 dark:border-zinc-600 text-transparent',
                      )}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span
                      className={cn(
                        'w-9 h-9 grid place-items-center rounded-md flex-shrink-0',
                        isDone
                          ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                          : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400',
                      )}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div
                        className={cn(
                          'text-sm font-medium',
                          isDone
                            ? 'text-zinc-500 dark:text-zinc-400 line-through'
                            : 'text-zinc-950 dark:text-zinc-50',
                        )}
                      >
                        {item.label}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                        {item.sub}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            className="text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
          >
            Skip for now
          </Link>
          <a
            href="https://khan-bakery.itcitysoft.com"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition"
          >
            Open POS
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Logo className="w-5 h-5 rounded-md" />
          <span>{brand.name} · always quietly competent</span>
        </div>
      </div>
    </div>
  );
}
